import React from "react";
import "./FactPicker.css";
import { OperationOptions, Operations } from "../../MathFactsGenerator/Constants.ts";
import { ISettingsProps } from "../../MathFactsGenerator/Models.ts";
import { labelFor } from "../../MathFactsGenerator/Labels.ts";

/** The four a game offers. Long division is left out: its answers can carry a remainder (ADR 0059 §4). */
export const FACT_OPERATIONS = [
    Operations.Addition,
    Operations.Subtraction,
    Operations.Multiplication,
    Operations.Division,
];

export function operationsOf(settings: ISettingsProps): string[] {
    return (settings.Operators ?? []).map((op) => op.value);
}

/** The printed sign for an operation, as a child would write it. */
export function signFor(operation: string): string {
    const sign = OperationOptions.find((op) => op.value === operation)?.sign ?? "";
    return sign === "x" ? "×" : sign === "-" ? "−" : sign;
}

/**
 * Buttons for which facts a game uses, shown before it starts rather than in
 * the settings panel, for a page named for math facts rather than one
 * operation (ADR 0059 §4, reused by 0060). The last one selected cannot be
 * switched off - a game needs facts.
 */
export function FactPicker({ settings, setSettings, label }: {
    settings: ISettingsProps;
    setSettings(settings: ISettingsProps): void;
    /** Accessible name for the group. */
    label: string;
}) {
    const chosen = operationsOf(settings);

    function toggle(operation: Operations) {
        const next = chosen.includes(operation)
            ? chosen.filter((op) => op !== operation)
            : [...chosen, operation];
        if (next.length === 0) {
            return;
        }
        setSettings({
            ...settings,
            Operators: OperationOptions.filter((op) => next.includes(op.value)),
        });
    }

    return (
        <div className="factPicker" role="group" aria-label={label}>
            {FACT_OPERATIONS.map((operation) => {
                const on = chosen.includes(operation);
                return (
                    <button
                        key={operation}
                        type="button"
                        className={`factPickerButton${on ? " factPickerButton--on" : ""}`}
                        aria-pressed={on}
                        onClick={() => toggle(operation)}
                    >
                        <span className="factPickerSign" aria-hidden="true">
                            {signFor(operation)}
                        </span>
                        <span className="factPickerName">{labelFor(operation, settings.Language)}</span>
                    </button>
                );
            })}
        </div>
    );
}
